import { memo, useState } from 'react';

import { Alert, Box, Button, ButtonGroup, Heading, Modal, Text, TextButton } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/web-base';

import { RentalManagerRenterRoleWebApiHandler_delete } from '@zg-rentals/ts-rental-manager-api-beta-client';

import type { Client } from '@zg-rentals/http-client';
import type { RentalManagerRenterRoleDeleteRequestDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

export type ModalDeleteTenantProps = {
  client: Client;
  isOpen: boolean;
  renterName: string;
  renterRoleIdsToDelete: Array<string>;
  onClose: () => void;
  onPrimaryCTAClick?: () => void;
  onSuccess?: () => void | null;
  onError?: (error: DatarouterError) => void | null;
};

/**
 * @param {ModalDeleteTenantProps} props
 *
 * @param {Client} props.client - The client to use for the query
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {RentalManagerRenterContactTenantDto} props.renter - The tenant to delete
 * @param {() => void} props.onClose - The function to call when the modal is closed
 * @param {() => void} props.onSuccess - The function to call when the tenant is successfully deleted
 * @param {() => void} props.onError - The function to call when the tenant is not successfully deleted
 *
 * @returns {JSX.Element}
 */
const ModalDeleteTenant = ({
  client,
  isOpen,
  renterName,
  renterRoleIdsToDelete,
  onClose,
  onSuccess,
  onError,
}: ModalDeleteTenantProps): JSX.Element => {
  const [serverError, setServerError] = useState<string | null>(null);

  const handleClose = (open: boolean) => {
    if (!open) {
      onClose?.();
    }
  };

  const handleConfirmClick = () => {
    const postData: RentalManagerRenterRoleDeleteRequestDto = {
      renterRoleIdsToDelete: renterRoleIdsToDelete,
    };

    setServerError(null);
    deleteTenant(postData);
  };

  const deleteTenant = async (postData: RentalManagerRenterRoleDeleteRequestDto) => {
    try {
      await RentalManagerRenterRoleWebApiHandler_delete(postData, client);

      handleSuccess();
    } catch (error) {
      const displayError = DatarouterError.getDisplayMessage(error);
      const errorMessage = displayError;

      const capitalizedError =
        typeof errorMessage === 'string' && errorMessage.length > 0
          ? errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)
          : 'An unexpected error occurred';

      setServerError(capitalizedError);

      onError?.(new DatarouterError(capitalizedError, 'DELETE_TENANT_ERROR', 500));

      logError({
        error: new Error(`Error adding tenants: ${capitalizedError}`),
        errorType: 'RenterRoleDeleteError',
        context: 'ModalDeleteTenant@deleteTenant',
      });
    }
  };

  const handleSuccess = () => {
    onSuccess?.();
    onClose?.();
  };

  const renderBody = () => {
    return (
      <Box data-testid="ModalDeleteTenant">
        {serverError && (
          <>
            <Box
              data-testid="ErrorContainer"
              css={{
                marginBottom: 'loose',
              }}
            >
              <Alert tone="warning">{serverError}</Alert>
            </Box>
          </>
        )}

        <Text>
          {renterName} will no longer be associated with this property. You can add them back later if you need to.
        </Text>
      </Box>
    );
  };

  return (
    <>
      <Modal
        open={isOpen}
        size={'md'}
        scrollDocument={false}
        header={<Heading level={2}>Remove this tenant?</Heading>}
        body={renderBody()}
        footer={
          <ButtonGroup aria-label="modal actions">
            <Modal.Close>
              <TextButton>Cancel</TextButton>
            </Modal.Close>
            <Button emphasis="filled" tone="brand" onClick={handleConfirmClick}>
              Yes, remove
            </Button>
          </ButtonGroup>
        }
        onOpenChange={handleClose}
      />
    </>
  );
};

export default memo(ModalDeleteTenant);
